<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Tendencia Diaria de Negocios',
  },
});

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}K`;
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

const maxValue = computed(() => {
  return Math.max(...props.data.map(d => d.total_value || 0), 1);
});

const maxDeals = computed(() => {
  return Math.max(...props.data.map(d => d.deals_count || 0), 1);
});

const totalValue = computed(() => {
  return props.data.reduce((acc, d) => acc + (d.total_value || 0), 0);
});

const totalDeals = computed(() => {
  return props.data.reduce((acc, d) => acc + (d.deals_count || 0), 0);
});

const avgDailyValue = computed(() => {
  if (!props.data.length) return 0;
  return totalValue.value / props.data.length;
});
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-calendar-range text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-4 text-xs text-n-slate-11">
        <span>Total:
          <strong class="text-n-teal-11">{{
            formatCurrency(totalValue)
          }}</strong></span>
        <span>Negocios:
          <strong class="text-n-blue-11">{{
            formatNumber(totalDeals)
          }}</strong></span>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div
      v-else-if="data.length === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Sem dados para o periodo</span>
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Chart -->
      <div class="flex items-end gap-1 h-40">
        <div
          v-for="day in data"
          :key="day.date"
          class="flex-1 flex flex-col items-center justify-end group relative"
        >
          <!-- Value Bar -->
          <div
            class="w-full rounded-t transition-all duration-300 bg-gradient-to-t from-n-teal-9 to-n-teal-7 hover:from-n-teal-10 hover:to-n-teal-8"
            :style="{
              height: `${(day.total_value / maxValue) * 100}%`,
              minHeight: day.total_value > 0 ? '4px' : '0',
            }"
          />

          <!-- Tooltip -->
          <div class="absolute bottom-full mb-2 hidden group-hover:block z-10">
            <div
              class="bg-n-solid-1 border border-n-weak rounded-lg px-3 py-2 text-xs shadow-lg whitespace-nowrap"
            >
              <div class="font-medium text-n-slate-12 mb-1">
                {{ formatDate(day.date) }}
              </div>
              <div class="flex flex-col gap-1">
                <div class="flex items-center justify-between gap-3">
                  <span class="text-n-slate-11">Negocios:</span>
                  <span class="font-medium text-n-blue-11">{{
                    formatNumber(day.deals_count)
                  }}</span>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <span class="text-n-slate-11">Valor:</span>
                  <span class="font-medium text-n-teal-11">{{
                    formatCurrency(day.total_value)
                  }}</span>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <span class="text-n-slate-11">Ganhos:</span>
                  <span class="font-medium text-n-teal-11">{{
                    formatNumber(day.won)
                  }}</span>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <span class="text-n-slate-11">Perdidos:</span>
                  <span class="font-medium text-n-ruby-11">{{
                    formatNumber(day.lost)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- X-axis labels -->
      <div class="flex gap-1">
        <div
          v-for="(day, idx) in data"
          :key="day.date"
          class="flex-1 text-center"
        >
          <span
            v-if="idx % Math.ceil(data.length / 7) === 0"
            class="text-xs text-n-slate-10"
          >
            {{ formatDate(day.date) }}
          </span>
        </div>
      </div>

      <!-- Summary -->
      <div class="grid grid-cols-3 gap-3 pt-3 border-t border-n-weak">
        <div class="text-center">
          <div class="text-xs text-n-slate-10 mb-1">Media Diaria</div>
          <div class="text-sm font-bold text-n-violet-11">
            {{ formatCurrency(avgDailyValue) }}
          </div>
        </div>
        <div class="text-center">
          <div class="text-xs text-n-slate-10 mb-1">Melhor Dia</div>
          <div class="text-sm font-bold text-n-teal-11">
            {{ formatCurrency(maxValue) }}
          </div>
        </div>
        <div class="text-center">
          <div class="text-xs text-n-slate-10 mb-1">Dias</div>
          <div class="text-sm font-bold text-n-slate-12">{{ data.length }}</div>
        </div>
      </div>

      <!-- Legend -->
      <div class="flex items-center justify-center gap-4 text-xs">
        <div class="flex items-center gap-1">
          <span
            class="w-3 h-3 rounded bg-gradient-to-t from-n-teal-9 to-n-teal-7"
          />
          <span class="text-n-slate-11">Valor do Dia</span>
        </div>
      </div>
    </div>
  </div>
</template>
